import { useState, Component } from 'react';
import axios from 'axios';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { RichTextEditor } from './RichTextEditor';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import {
  GripVertical,
  ChevronDown,
  ChevronUp,
  Trash2,
  Plus,
  Image as ImageIcon,
  X,
  FileText,
  Columns,
} from 'lucide-react';
import { toast } from 'sonner';
import { getApiBaseUrl } from '@/lib/apiBase';

const API_URL = getApiBaseUrl();

const genId = () => `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`;

export const migrateToBlocks = (description) => {
  if (Array.isArray(description) && description.length > 0) return description;
  return [{
    id: genId(),
    type: 'rich_text',
    content: typeof description === 'string' ? description : '',
    image_url: null,
    image_caption: null,
  }];
};

export const makeRichTextBlock = () => ({
  id: genId(),
  type: 'rich_text',
  content: '',
  image_url: null,
  image_caption: null,
});

export const makeSplitViewBlock = () => ({
  id: genId(),
  type: 'split_view',
  content: '',
  image_url: null,
  image_caption: null,
});

// ─── Error boundary: one broken block must never freeze the whole modal ─────

class BlockErrorBoundary extends Component {
  state = { error: null };

  static getDerivedStateFromError(error) {
    return { error };
  }

  render() {
    if (this.state.error) {
      return (
        <div
          style={{
            padding: 16,
            background: '#fff3f3',
            border: '1px solid #ffcccc',
            borderRadius: 8,
          }}
        >
          ⚠️ This block failed to load.{' '}
          <button type="button" onClick={() => this.setState({ error: null })}>
            Retry
          </button>
          <pre style={{ fontSize: 11, color: '#cc0000' }}>{this.state.error.message}</pre>
        </div>
      );
    }
    return this.props.children;
  }
}

// ─── Single sortable block ────────────────────────────────────────────────────

const SortableBlock = ({ block, isOnly, onUpdate, onDelete, token }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: block.id });
  const [collapsed, setCollapsed] = useState(false);
  const [uploading, setUploading] = useState(false);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    setUploading(true);
    try {
      const res = await axios.post(`${API_URL}/admin/upload/image`, fd, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      onUpdate({ image_url: res.data.url || `/api/media/images/${res.data.filename}` });
      toast.success('Image uploaded');
    } catch {
      toast.error('Image upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const isSplit = block.type === 'split_view';

  return (
    <div ref={setNodeRef} style={style} className="border rounded-lg bg-background shadow-sm">
      {/* Block header bar */}
      <div className="flex items-center gap-2 px-3 py-2 border-b bg-muted/30 rounded-t-lg select-none">
        <button
          type="button"
          className="cursor-grab touch-none text-muted-foreground hover:text-foreground"
          aria-label="Drag to reorder"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="w-4 h-4" />
        </button>

        <Badge variant={isSplit ? 'default' : 'secondary'} className="text-xs">
          {isSplit ? (
            <><Columns className="w-3 h-3 mr-1" />Split View</>
          ) : (
            <><FileText className="w-3 h-3 mr-1" />Rich Text</>
          )}
        </Badge>

        <div className="flex-1" />

        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-6 w-6"
          onClick={() => setCollapsed((c) => !c)}
          aria-label={collapsed ? 'Expand block' : 'Collapse block'}
        >
          {collapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
        </Button>

        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-6 w-6"
          disabled={isOnly}
          onClick={onDelete}
          title={isOnly ? 'Cannot delete the only block' : 'Delete block'}
        >
          <Trash2 className={`w-4 h-4 ${isOnly ? 'text-muted-foreground' : 'text-destructive'}`} />
        </Button>
      </div>

      {/* Block content */}
      {!collapsed && (
        <div className="p-4">
          {isSplit ? (
            <div className="grid grid-cols-2 gap-4">
              {/* Left: text editor */}
              <div>
                <Label className="mb-2 block text-sm font-medium text-muted-foreground">
                  Text Content
                </Label>
                <RichTextEditor
                  value={block.content}
                  onChange={(val) => onUpdate({ content: val })}
                  placeholder="Write text content here..."
                />
              </div>

              {/* Right: image upload */}
              <div className="space-y-3">
                <Label className="block text-sm font-medium text-muted-foreground">
                  Image
                </Label>

                {block.image_url ? (
                  <div className="space-y-2">
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL || ''}${block.image_url}`}
                      alt="Block image preview"
                      className="w-full h-40 object-cover rounded-lg border"
                    />
                    <div className="flex gap-2">
                      <label className="flex-1 cursor-pointer">
                        <div className="flex items-center justify-center gap-1 px-3 py-1.5 text-sm border rounded-md hover:bg-accent transition-colors">
                          <ImageIcon className="w-3 h-3" />
                          {uploading ? 'Uploading…' : 'Change Image'}
                        </div>
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={handleImageUpload}
                          disabled={uploading}
                        />
                      </label>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => onUpdate({ image_url: null })}
                        title="Remove image"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <label className="block border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-primary/50 transition-colors">
                    {uploading ? (
                      <span className="text-sm text-muted-foreground">Uploading…</span>
                    ) : (
                      <>
                        <ImageIcon className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">
                          Click to upload image
                        </span>
                      </>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleImageUpload}
                      disabled={uploading}
                    />
                  </label>
                )}

                <div>
                  <Label className="text-xs text-muted-foreground mb-1 block">
                    Caption (optional)
                  </Label>
                  <Input
                    value={block.image_caption || ''}
                    onChange={(e) => onUpdate({ image_caption: e.target.value })}
                    placeholder="Image caption…"
                  />
                </div>
              </div>
            </div>
          ) : (
            <RichTextEditor
              value={block.content}
              onChange={(val) => onUpdate({ content: val })}
              placeholder="Write lesson content with rich formatting…"
            />
          )}
        </div>
      )}
    </div>
  );
};

// ─── "Add Block" button between blocks ───────────────────────────────────────

const AddBlockButton = ({ onAdd }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="flex justify-center py-1">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 px-3 text-xs text-muted-foreground hover:text-foreground"
          >
            <Plus className="w-3 h-3 mr-1" />
            Add Block
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-44 p-1" align="center">
          <button
            type="button"
            className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded hover:bg-accent transition-colors"
            onClick={() => { onAdd('rich_text'); setOpen(false); }}
          >
            <FileText className="w-4 h-4 text-muted-foreground" />
            Rich Text
          </button>
          <button
            type="button"
            className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded hover:bg-accent transition-colors"
            onClick={() => { onAdd('split_view'); setOpen(false); }}
          >
            <Columns className="w-4 h-4 text-muted-foreground" />
            Split View
          </button>
        </PopoverContent>
      </Popover>
    </div>
  );
};

// ─── Main exported component ──────────────────────────────────────────────────

const LessonContentEditor = ({ blocks, onChange, token }) => {
  const sensors = useSensors(useSensor(PointerSensor));

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = blocks.findIndex((b) => b.id === active.id);
    const newIndex = blocks.findIndex((b) => b.id === over.id);
    onChange(arrayMove(blocks, oldIndex, newIndex));
  };

  const updateBlock = (id, updates) => {
    onChange(blocks.map((b) => (b.id === id ? { ...b, ...updates } : b)));
  };

  const deleteBlock = (id) => {
    if (blocks.length <= 1) return;
    onChange(blocks.filter((b) => b.id !== id));
  };

  const addBlock = (afterIndex, type) => {
    const newBlock = type === 'split_view' ? makeSplitViewBlock() : makeRichTextBlock();
    const next = [...blocks];
    next.splice(afterIndex + 1, 0, newBlock);
    onChange(next);
  };

  return (
    <div className="space-y-0">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={blocks.map((b) => b.id)}
          strategy={verticalListSortingStrategy}
        >
          {blocks.map((block, index) => (
            <div key={block.id}>
              <BlockErrorBoundary>
                <SortableBlock
                  block={block}
                  isOnly={blocks.length === 1}
                  onUpdate={(updates) => updateBlock(block.id, updates)}
                  onDelete={() => deleteBlock(block.id)}
                  token={token}
                />
              </BlockErrorBoundary>
              <AddBlockButton onAdd={(type) => addBlock(index, type)} />
            </div>
          ))}
        </SortableContext>
      </DndContext>
    </div>
  );
};

export default LessonContentEditor;
